import { useEffect, useRef } from 'react';

import { useFormContext } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { TMobileSignupForm } from '../form/form';
import { furthestAllowedIndex } from '../form/resume';
import { useMobileSignupResume } from '../form/useMobileSignupResume';
import { SCREEN_REGISTRY } from '../screens/registry';
import { TScreenDef } from '../screens/types';

export type TNavDirection = 'forwards' | 'backwards';

export type TMobileSignupNav =
  | { status: 'pending' }
  | {
      status: 'ready';
      index: number;
      screen: TScreenDef;
      direction: TNavDirection;
      goNext: () => void;
      goBack?: () => void;
    };

export const useMobileSignupNav = (): TMobileSignupNav => {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { getValues } = useFormContext<TMobileSignupForm>();
  const resume = useMobileSignupResume();

  const screenId = hash.replace(/^#/, '');
  const hashIndex = SCREEN_REGISTRY.findIndex(
    (screen) => screen.id === screenId,
  );
  const lastIndex = SCREEN_REGISTRY.length - 1;

  let resolvedIndex: number;
  let needsRedirect = false;
  if (resume.status === 'complete') {
    resolvedIndex = lastIndex;
    needsRedirect = hashIndex !== lastIndex;
  } else {
    const ceiling = furthestAllowedIndex(getValues(), SCREEN_REGISTRY);
    if (hashIndex === -1 || hashIndex > ceiling) {
      resolvedIndex = ceiling;
      needsRedirect = true;
    } else {
      resolvedIndex = hashIndex;
    }
  }

  const targetId = SCREEN_REGISTRY[resolvedIndex]?.id;

  useEffect(() => {
    if (resume.status === 'pending') return;
    if (needsRedirect && targetId) {
      navigate({ hash: `#${targetId}` }, { replace: true });
    }
  }, [resume.status, needsRedirect, targetId, navigate]);

  const prevIndexRef = useRef(resolvedIndex);
  const direction: TNavDirection =
    resolvedIndex >= prevIndexRef.current ? 'forwards' : 'backwards';
  // Record this render's index after commit so the next render can compare against it.
  useEffect(() => {
    prevIndexRef.current = resolvedIndex;
  });

  if (resume.status === 'pending') {
    return { status: 'pending' };
  }

  const goNext = () => {
    const nextId = SCREEN_REGISTRY[resolvedIndex + 1]?.id;
    if (nextId) navigate({ hash: `#${nextId}` });
  };

  const goBack =
    resolvedIndex > 0
      ? () => {
          const prevId = SCREEN_REGISTRY[resolvedIndex - 1]?.id;
          if (prevId) navigate({ hash: `#${prevId}` });
        }
      : undefined;

  return {
    status: 'ready',
    index: resolvedIndex,
    screen: SCREEN_REGISTRY[resolvedIndex],
    direction,
    goNext,
    goBack,
  };
};
